import { useSearchUsersQuery, type User } from '@paintscout/api'
import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { removeAuth0Prefix } from '@paintscout/util'
import * as Sentry from '@sentry/react'
import { useUser } from '../UserProvider'
import { useClientOptions } from '../ClientOptionsProvider'
import { buildScopedUserQuery } from '../../util/buildScopedUserQuery'

interface UsersContextValue {
  users?: User[]
  /**
   * `users` keyed by prefix-less id, matching the `owner.id`/`assignedUsers` keys stored on
   * documents. Memoized here so every consumer that needs to resolve an id -> user shares one
   * map instead of rebuilding it (see getOwnerDisplayName).
   */
  usersById?: Record<string, User>
  userPictures?: Record<string, string>
  loading?: boolean
  refetchUsers?: () => void
}

export const UsersContext = createContext<UsersContextValue | undefined>(undefined)

export const UsersProvider: React.FC<{
  children: React.ReactNode
  includeCrew?: boolean
}> = ({ children, includeCrew }) => {
  const { clientId } = useClientOptions()
  const { isSuperadmin, isSupport, user } = useUser()

  // includeInactive: true below already asks the server for inactive users; without it here too,
  // a masquerading support/superadmin session's own query string still forced active:true (the
  // GraphQL argument alone doesn't help — enforceUserCompanyScope, which scopes their query,
  // treats an argument-only request as absent unless the query string agrees). That gap used to be
  // harmless because nothing could select an inactive estimator, but the Estimator report filter
  // now can (#23701) — left unfixed, this hook's `knownUserIds` (used to reconcile persisted
  // filters, see reconcileUserFilters) would silently wipe that selection on the next render, the
  // exact self-healing-gone-wrong loop docs/pitfalls/README.md warns about.
  const usersQuery = buildScopedUserQuery({ isPrivileged: isSuperadmin || isSupport, clientId, includeInactive: true })

  const { data, loading, refetch } = useSearchUsersQuery({
    variables: {
      query: usersQuery,
      includeInactive: true,
      includeCrew
    },
    skip: !user,
    // cache-and-network: render instantly from the persisted IDB cache (apollo serves the
    // cached result synchronously into `data`, so there's no blocking load even though this
    // provider wraps the whole app) while a single background fetch refreshes the list. This is
    // required for cross-session/cross-app changes — e.g. a painter updating their headshot in
    // painter-view — to surface on the main account. With cache-first those changes never
    // appeared here (the cached list was served even across restarts) because nothing in the
    // main app invalidates it. Same-app freshness also still comes from refetchUsers()
    // (user-mutation flows) and network-only management surfaces (Team/TeamNext).
    // Read `data` directly rather than via onCompleted: onCompleted only fires once the network
    // request resolves, which would discard the synchronously-available cached rows.
    fetchPolicy: 'cache-and-network'
  })

  // Keep a stable reference: data?.searchUsers?.rows ?? [] would mint a new array every render,
  // re-running userPictures' memo and re-rendering every consumer reading `users` from context.
  const rows = data?.searchUsers?.rows
  const users = useMemo(() => rows ?? [], [rows])

  const usersById = useMemo(
    () =>
      users.reduce<Record<string, User>>((acc, user) => {
        acc[removeAuth0Prefix(user.user_id)] = user
        return acc
      }, {}),
    [users]
  )

  // Wrap refetch so a mid-session network failure is surfaced to Sentry instead of
  // becoming an unhandled rejection that silently leaves the cached list stale.
  const refetchUsers = useCallback(
    () =>
      refetch?.().catch((err) => {
        Sentry.captureException(err)
      }),
    [refetch]
  )

  return (
    <UsersContext.Provider
      value={{
        // Only surface loading when we have nothing to show yet. With cache-and-network apollo
        // keeps `loading` true during the background refresh even though cached rows are already
        // available — without this guard, consumers that gate spinners on `loading` would flash
        // a spinner over already-rendered data on every load.
        loading: loading && !data,
        users,
        usersById,
        userPictures: useMemo(
          () =>
            users?.reduce(
              (acc, user) => {
                acc[user.user_id] =
                  user.user_metadata?.customAvatar?.cdnValue ?? user.user_metadata?.customAvatar?.value
                acc[removeAuth0Prefix(user.user_id)] =
                  user.user_metadata?.customAvatar?.cdnValue ?? user.user_metadata?.customAvatar?.value

                return acc
              },
              {} as Record<string, string>
            ),
          [users]
        ),
        refetchUsers
      }}
    >
      {children}
    </UsersContext.Provider>
  )
}

export const useUsers = (): UsersContextValue => {
  const context = useContext(UsersContext)
  if (!context) {
    throw new Error('useUsers must be used within a UsersProvider')
  }
  return context
}
